import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  IMasterTranslation,
  IRenameMasterKeyRequest,
  ISyncTranslationsRequest,
  ISyncTranslationsResult,
  IUpsertMasterTranslationRequest,
} from "../../../@types/admin/AdminMasterTranslations";
import {
  masterTranslationsService,
  translationsSyncService,
} from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import {
  masterTranslationKeysKey,
  masterTranslationsByKeyKey,
  translationsSyncStatusKey,
  translationsSyncSummaryKey,
} from "../../queries/admin/masterTranslations";

/** Editor mutations. Every one invalidates the key list; per-key detail too. */
export function useMasterTranslationMutations(selectedKey: string | undefined) {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: masterTranslationKeysKey });
    if (selectedKey) {
      queryClient.invalidateQueries({
        queryKey: masterTranslationsByKeyKey(selectedKey),
      });
    }
  };

  const upsert = useMutation<
    IMasterTranslation,
    Error,
    IUpsertMasterTranslationRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await masterTranslationsService.upsertTranslation(request)),
    onSuccess: invalidate,
  });

  const renameKey = useMutation<void, Error, IRenameMasterKeyRequest>({
    mutationFn: async (request) =>
      ensureOk(await masterTranslationsService.renameKey(request)),
    onSuccess: invalidate,
  });

  const deleteKey = useMutation<void, Error, string>({
    mutationFn: async (key) =>
      ensureOk(await masterTranslationsService.deleteKey(key)),
    onSuccess: invalidate,
  });

  const deleteTranslation = useMutation<
    void,
    Error,
    { key: string; languageCode: string; usedLocation: string }
  >({
    mutationFn: async ({ key, languageCode, usedLocation }) =>
      ensureOk(
        await masterTranslationsService.deleteTranslation(
          key,
          languageCode,
          usedLocation
        )
      ),
    onSuccess: invalidate,
  });

  return {
    upsertTranslation: upsert.mutateAsync,
    isUpserting: upsert.isPending,
    renameKey: renameKey.mutateAsync,
    isRenaming: renameKey.isPending,
    deleteKey: deleteKey.mutateAsync,
    isDeletingKey: deleteKey.isPending,
    deleteTranslation: deleteTranslation.mutateAsync,
    isDeletingTranslation: deleteTranslation.isPending,
  };
}

/** Pulls master translations into the current environment's Global DB. */
export function useSyncTranslations() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ISyncTranslationsResult,
    Error,
    ISyncTranslationsRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await translationsSyncService.sync(request)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: translationsSyncStatusKey });
      queryClient.invalidateQueries({ queryKey: translationsSyncSummaryKey });
      // Any open key-detail comparison is stale after a sync.
      queryClient.invalidateQueries({
        queryKey: ["admin-translations-sync-key-detail"],
      });
    },
  });

  return { syncTranslations: mutateAsync, isSyncing: isPending };
}
